import { TableCell, TableRow } from '@savvy-kit/components/table';
import { Text } from '@savvy-kit/foundations/text';
import { isNil } from 'es-toolkit';

import { formatDollarAmount } from '../../../../utils/formatting';

export type TaxTranscriptData = {
  id: string;
  taxYear: number;
  totalIncome?: number | null;
  federalTaxLiability?: number | null;
  federalRefund?: number | null;
  totalDeductions?: number | null;
  yoyChange?: {
    totalIncomePct?: number | null;
    federalTaxLiabilityPct?: number | null;
    federalRefundPct?: number | null;
    totalDeductionsPct?: number | null;
  } | null;
};

type TaxRowProps = {
  readonly label: string;
  readonly dotClassName: string;
  readonly transcripts: TaxTranscriptData[];
  readonly getValue: (t: TaxTranscriptData) => number | null | undefined;
  readonly getPct?: (t: TaxTranscriptData) => number | null | undefined;
  readonly shouldColorValue?: boolean;
  readonly hidePct?: boolean;
};

export function TaxHistoryRow({
  label,
  dotClassName,
  transcripts,
  getValue,
  getPct,
  shouldColorValue,
  hidePct = false,
}: TaxRowProps) {
  const formatCurrency = (value?: number | null) => {
    if (isNil(value)) return '-';
    return formatDollarAmount(value, false);
  };

  const getAmountColor = (value?: number | null) => {
    if (!shouldColorValue || isNil(value)) return 'text-color-primary';
    return value >= 0 ? 'text-success' : 'text-destructive';
  };

  const getPercentageColor = (value?: number | null) => {
    if (isNil(value)) return 'text-secondary';
    return value >= 0 ? 'text-success' : 'text-destructive';
  };

  return (
    <TableRow className="hover:bg-slate-50/50 transition-colors">
      <TableCell className="px-6 align-top">
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-3 h-[24px]">
            <div className={`h-3.5 w-3.5 rounded shrink-0 ${dotClassName}`} />
            <Text variant="subtitle-sm">{label}</Text>
          </div>
          <div className="min-h-[20px]" />
        </div>
      </TableCell>

      {transcripts.map((t) => {
        const val = getValue(t);
        const pct = hidePct ? null : getPct?.(t);

        return (
          <TableCell key={t.id} className="pt-4 pb-0 px-6 align-top text-right tabular-nums">
            <div className="flex flex-col items-end gap-1">
              <div className={getAmountColor(val)}>
                {shouldColorValue && !isNil(val) && val >= 0 ? '+ ' : ''}
                {formatCurrency(val)}
              </div>

              {/* min-h-5 ensures the row doesn't collapse if pct is missing */}
              <div className="min-h-[20px]">
                {!isNil(pct) ? (
                  <div className={`text-xs font-medium ${getPercentageColor(pct)}`}>
                    {pct >= 0 ? '+ ' : '- '}
                    {Math.abs(pct)}%
                  </div>
                ) : null}
              </div>
            </div>
          </TableCell>
        );
      })}
    </TableRow>
  );
}
